import {
    BankingGeneralDisclosure,
    CashApyDisclosure,
    CashBackDisclosure,
} from '@fruitful/compliance';
import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    BILLS_SYSTEM_COLOR,
    GOALS_COLOR,
    GOALS_SYSTEM_COLOR,
    GoalsSystemContent,
    INCOME_BG,
    INCOME_SYSTEM_BG,
    IncomeSystemContent,
    MonthlyExpensesSystemContent,
    SPEND_SYSTEM_COLOR,
    SYSTEM_GAP,
    toHex,
} from '@fruitful/money-map';
import { type RuleSetDisplay } from '@fruitful/types';
import { LinearGradient } from 'expo-linear-gradient';
import React, {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import Animated, {
    Easing,
    Extrapolation,
    interpolate,
    interpolateColor,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withRepeat,
    withSequence,
    withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodyLargeEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    FontFamilyName,
    FruitfulLogo,
    H5Emphasized,
    Image,
    isWeb,
    ScrollView,
    Text,
    View,
    XStack,
    YStack,
} from 'smoothie';

import ChargeCardIcon from '../../assets/images/charge-card.png';

const serifBody = {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: FontFamilyName.SourceSerifRegular,
} as const;

// Timing for the looping money-map animation that drives the shared system
// box components (income bar slide, connector dots, bar fills).
const COLLAPSIBLE_BASE_DELAY = 200;
// Two-phase collapsible timing: the gap slides open/shut (which is what pushes
// the money-map boxes apart / back together) separately from the copy fade, so
// on open the boxes break apart first and the text fills the gap after, and on
// close the text disappears first and then the boxes collapse back together.
const COLLAPSIBLE_HEIGHT_MS = 360;
const COLLAPSIBLE_FADE_MS = 220;
// On close, the boxes finish sliding back together once the last collapsible
// above the bottom box has collapsed (base delay + last heading stagger of 350
// + copy fade + gap slide). Only reveal the vertical connector bars after that
// so they don't draw through boxes that are still mid-slide. Small buffer so
// the settled box positions have been re-measured.
const CONNECTOR_REVEAL_DELAY_MS =
    COLLAPSIBLE_BASE_DELAY +
    350 +
    COLLAPSIBLE_FADE_MS +
    COLLAPSIBLE_HEIGHT_MS +
    60;

// Time (at speedScale = 1) for the copy to fully collapse and the boxes to
// finish sliding back together: the last collapsible's stagger (base + 420) +
// its copy fade + its gap slide. Consumers scale this by the same `speedScale`
// they pass to the body to know when the collapse has settled.
export const EXPLAINER_COLLAPSE_BASE_MS =
    COLLAPSIBLE_BASE_DELAY + 420 + COLLAPSIBLE_FADE_MS + COLLAPSIBLE_HEIGHT_MS;
const INCOME_SLIDE_MS = 900;
const DOT_PHASE_MS = 2400;
const LOOP_REST_MS = 700;
const DOT_PHASE_END = 2;
const FORK_TRIGGER_THRESHOLD = 0.1;
const FORK_RANGE = 0.15;
const BAR_FILL_RANGE = 0.15;

const buildLoop = () =>
    withRepeat(
        withSequence(
            withTiming(0, { duration: 0 }),
            withTiming(1, {
                duration: INCOME_SLIDE_MS,
                easing: Easing.inOut(Easing.ease),
            }),
            withTiming(DOT_PHASE_END, {
                duration: DOT_PHASE_MS,
                easing: Easing.inOut(Easing.ease),
            }),
            withDelay(LOOP_REST_MS, withTiming(0, { duration: 0 })),
        ),
        -1,
        false,
    );

// Vertical connector between the label boxes (only drawn when the copy is
// collapsed). Matches the money-map preview's white line + traveling dot.
const CONNECTOR_WIDTH = 4;
const CONNECTOR_DOT_SIZE = 24;
const INCOME_HEX = toHex(INCOME_BG);

/**
 * One clipped window over a single connector segment. A single dot travels the
 * full connector (in absolute container coords) and each segment only reveals
 * it while it passes through that gap — so it appears to "flow" between boxes
 * and hide behind the label chips, exactly like the preview.
 */
const ConnectorDot = ({
    seg,
    evenThresholds,
    centerYs,
    colorBreakT,
    labelCenterX,
    loopProgress,
    goalsHex,
}: {
    seg: { top: number; height: number };
    evenThresholds: number[];
    centerYs: number[];
    colorBreakT: number;
    labelCenterX: number;
    loopProgress: Animated.SharedValue<number>;
    goalsHex: string;
}) => {
    const dotStyle = useAnimatedStyle(() => {
        const t = Math.max(0, Math.min(1, loopProgress.value - 1));
        const globalY = interpolate(
            t,
            evenThresholds,
            centerYs,
            Extrapolation.CLAMP,
        );
        const visible = loopProgress.value >= 1 && t > 0.001 && t < 0.999;
        return {
            opacity: visible ? 1 : 0,
            transform: [
                { translateY: globalY - seg.top - CONNECTOR_DOT_SIZE / 2 },
            ],
            backgroundColor: interpolateColor(
                t,
                [0, colorBreakT, colorBreakT + 0.01, 1],
                [INCOME_HEX, INCOME_HEX, goalsHex, goalsHex],
            ),
        };
    });

    return (
        <View
            pointerEvents="none"
            style={{
                position: 'absolute',
                left: labelCenterX - CONNECTOR_WIDTH / 2,
                top: seg.top,
                width: CONNECTOR_WIDTH,
                height: Math.max(0, seg.height),
                overflow: 'hidden',
                zIndex: 6,
            }}
        >
            <Animated.View
                style={[
                    {
                        width: CONNECTOR_WIDTH,
                        height: CONNECTOR_DOT_SIZE,
                        borderRadius: CONNECTOR_WIDTH / 2,
                    },
                    dotStyle,
                ]}
            />
        </View>
    );
};

const SectionHeading = ({ title, body }: { title: string; body: string }) => (
    <YStack gap={4}>
        <BodyLargeEmphasized>{title}</BodyLargeEmphasized>
        <Text {...serifBody}>{body}</Text>
    </YStack>
);

const ProductRow = ({
    icon,
    title,
    subtitle,
    amount,
}: {
    icon: ReactNode;
    title: string;
    subtitle: string;
    amount: string;
}) => (
    <XStack
        backgroundColor="$gray200"
        borderRadius={16}
        padding={16}
        gap={12}
        alignItems="center"
    >
        {icon}
        <YStack flex={1} gap={4}>
            <Text
                fontSize={16}
                lineHeight={24}
                color="$gray1200"
                fontWeight="500"
            >
                {title}
            </Text>
            <Text fontSize={14} lineHeight={20} color="$gray1000">
                {subtitle}
            </Text>
        </YStack>
        <Text fontSize={16} lineHeight={24} color="$gray1200" fontWeight="500">
            {amount}
        </Text>
    </XStack>
);

/**
 * Expands/collapses its children in place with a cute animation. The content is
 * always mounted (so the surrounding money-map boxes never remount) — it simply
 * animates its height/opacity between hidden and shown when `open` flips.
 * (Web transition based; on native it falls back to showing/hiding instantly.)
 */
const Collapsible = ({
    open,
    delay = 0,
    scale = 1,
    children,
}: {
    open: boolean;
    delay?: number;
    /** Multiplies every delay/duration so callers can speed the whole staggered
     * open/close up or down (e.g. narrow runs faster than wide). */
    scale?: number;
    children: React.ReactNode;
}) => {
    const d = delay * scale;
    const heightMs = COLLAPSIBLE_HEIGHT_MS * scale;
    const fadeMs = COLLAPSIBLE_FADE_MS * scale;
    const [height, setHeight] = useState(0);
    const [measured, setMeasured] = useState(false);
    const mountedOpen = React.useRef(open);
    const [entered, setEntered] = useState(!mountedOpen.current);
    const skipMaxHeight = !measured && mountedOpen.current;

    useEffect(() => {
        if (mountedOpen.current && !entered) {
            requestAnimationFrame(() => {
                setEntered(true);
            });
        }
    }, []);

    // Gate the expanded max-height on both a measured height AND a painted
    // collapsed frame. Without this, the first (cold) open flips `open` before
    // onLayout has measured the content, so max-height jumps 0 → real height
    // with no transition and the boxes just "appear" lower instead of sliding.
    const [expanded, setExpanded] = useState(false);
    useEffect(() => {
        if (open && measured) {
            const raf = requestAnimationFrame(() => {
                setExpanded(true);
            });
            return () => {
                cancelAnimationFrame(raf);
            };
        }
        setExpanded(false);
        return undefined;
    }, [open, measured]);

    const isVisible = skipMaxHeight ? entered : open;

    // On native, CSS transitions and string transforms don't work. Fall back
    // to an instant show/hide so the section copy actually renders.
    if (!isWeb) {
        return (
            <View
                style={{ overflow: 'hidden', display: open ? 'flex' : 'none' }}
            >
                <YStack paddingBottom={16}>{children}</YStack>
            </View>
        );
    }

    return (
        <View
            style={{
                overflow: 'hidden',
                ...(skipMaxHeight
                    ? {
                          opacity: isVisible ? 1 : 0,
                          transform: isVisible
                              ? 'translateY(0px)'
                              : 'translateY(8px)',
                          transition: `opacity ${300 * scale}ms ease ${d}ms, transform ${300 * scale}ms ease ${d}ms`,
                      }
                    : {
                          maxHeight: expanded ? height : 0,
                          opacity: expanded ? 1 : 0,
                          transform: expanded
                              ? 'translateY(0px)'
                              : 'translateY(-6px)',
                          transition: open
                              ? // OPEN: the gap slides open first (boxes break
                                // apart), then the copy fades into the gap.
                                `max-height ${heightMs}ms cubic-bezier(0.22, 1, 0.36, 1) ${d}ms, opacity ${fadeMs}ms ease ${d + heightMs}ms, transform ${fadeMs}ms ease ${d + heightMs}ms`
                              : // CLOSE: the copy fades out first, then the gap
                                // slides shut (boxes collapse back together).
                                `max-height ${heightMs}ms cubic-bezier(0.22, 1, 0.36, 1) ${d + fadeMs}ms, opacity ${fadeMs}ms ease ${d}ms, transform ${fadeMs}ms ease ${d}ms`,
                      }),
            }}
        >
            <YStack
                paddingBottom={16}
                onLayout={(e) => {
                    setHeight(e.nativeEvent.layout.height);
                    if (!measured) setMeasured(true);
                }}
            >
                {children}
            </YStack>
        </View>
    );
};

/**
 * The interwoven "how your system works" content. Reuses the exact same
 * money-map box components the preview renders (`IncomeSystemContent`,
 * `MonthlyExpensesSystemContent`, `GoalsSystemContent`) and weaves the section
 * copy, product rows, and disclosures between them. Rendered inline inside the
 * System step's left column when the explainer is toggled on.
 */
export const HowSystemWorksBody = ({
    incomeTotalCents,
    billsTotalCents,
    spendTotalCents,
    goalsTotalCents,
    ruleSetDisplays,
    showText = true,
    speedScale = 1,
}: {
    incomeTotalCents: number;
    billsTotalCents: number;
    spendTotalCents: number;
    goalsTotalCents: number;
    ruleSetDisplays: RuleSetDisplay[] | null;
    /** When false, the woven copy is collapsed and only the money map shows. */
    showText?: boolean;
    /** Multiplies every open/close animation delay + duration (e.g. narrow
     * runs faster than wide). */
    speedScale?: number;
}) => {
    const rules = ruleSetDisplays ?? [];

    // --- Shared measurement state (mirrors the preview's system layout) ---
    // Label boxes (INCOME / MONTHLY EXPENSES / GOAL) all size to the widest.
    const [labelWidths, setLabelWidths] = useState<[number, number, number]>([
        0, 0, 0,
    ]);
    const updateLabelWidth = useCallback((index: number, width: number) => {
        setLabelWidths((prev) => {
            if ((prev[index] ?? 0) === width) return prev;
            const next: [number, number, number] = [...prev];
            next[index] = width;
            return next;
        });
    }, []);
    const maxLabelWidth = Math.max(
        labelWidths[0],
        labelWidths[1],
        labelWidths[2],
    );
    const [braceHeight, setBraceHeight] = useState(0);

    // --- Connector-line measurement state ---
    // Label height + y-within-row for each section label (income / bills /
    // goal-i), the goal row heights, and each box's top offset within this
    // container. Together they let us rebuild the vertical connector between the
    // label chips (only shown while the copy is collapsed).
    const [labelInfo, setLabelInfo] = useState<
        Record<string, { height: number; yInRow: number }>
    >({});
    const updateLabelHeight = useCallback(
        (key: string, height: number, yInRow: number) => {
            setLabelInfo((prev) => {
                const existing = prev[key];
                if (
                    existing?.height === height &&
                    existing?.yInRow === yInRow
                ) {
                    return prev;
                }
                return { ...prev, [key]: { height, yInRow } };
            });
        },
        [],
    );
    const [goalRowHeights, setGoalRowHeights] = useState<
        Record<number, number>
    >({});
    const updateGoalRowHeight = useCallback((index: number, height: number) => {
        setGoalRowHeights((prev) => {
            if (prev[index] === height) return prev;
            return { ...prev, [index]: height };
        });
    }, []);
    const [boxTops, setBoxTops] = useState<{
        income: number;
        expenses: number;
        goals: number;
    }>({ income: 0, expenses: 0, goals: 0 });
    const updateBoxTop = useCallback(
        (key: 'income' | 'expenses' | 'goals', y: number) => {
            setBoxTops((prev) =>
                prev[key] === y ? prev : { ...prev, [key]: y },
            );
        },
        [],
    );

    // --- Looping animation shared values (drive the box internals) ---
    const loopProgressA = useSharedValue(0);
    const loopProgressB = useSharedValue(0);
    const activeLoopIdx = useSharedValue(0);
    // Keep goal bars in their steady (filled) state.
    const filledGoalCount = useSharedValue(rules.length);

    useEffect(() => {
        // Drive both SVs in sync so every goal group (even + odd index) fills.
        loopProgressA.value = buildLoop();
        loopProgressB.value = buildLoop();
    }, []);

    // The connector bars are only drawn in the collapsed state. Hide them while
    // the copy is open/expanding, and — crucially — keep them hidden while the
    // boxes are sliding back together on close, only revealing them once the
    // boxes have settled into their final positions.
    const [connectorVisible, setConnectorVisible] = useState(false);
    useEffect(() => {
        if (showText) {
            setConnectorVisible(false);
            return;
        }
        const timer = setTimeout(() => {
            setConnectorVisible(true);
        }, CONNECTOR_REVEAL_DELAY_MS * speedScale);
        return () => {
            clearTimeout(timer);
        };
    }, [showText, speedScale]);

    const goalForkThresholds = useMemo(
        () => rules.map((_, i) => Math.min(0.15 + i * 0.18, 0.9)),
        [rules],
    );

    const incomeBox = (
        <View backgroundColor={INCOME_SYSTEM_BG} borderRadius={16} padding={8}>
            <IncomeSystemContent
                incomeTotalCents={incomeTotalCents}
                maxLabelWidth={maxLabelWidth}
                updateLabelWidth={updateLabelWidth}
                updateLabelHeight={updateLabelHeight}
                loopProgressA={loopProgressA}
                loopProgressB={loopProgressB}
                activeLoopIdx={activeLoopIdx}
            />
        </View>
    );

    const expensesBox = (
        <View borderRadius={16} overflow="hidden" padding={8}>
            <LinearGradient
                colors={[toHex(BILLS_SYSTEM_COLOR), toHex(SPEND_SYSTEM_COLOR)]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                }}
            />
            <MonthlyExpensesSystemContent
                billsTotalCents={billsTotalCents}
                spendTotalCents={spendTotalCents}
                maxLabelWidth={maxLabelWidth}
                updateLabelWidth={updateLabelWidth}
                updateLabelHeight={updateLabelHeight}
                braceHeight={braceHeight}
                setBraceHeight={setBraceHeight}
                loopProgressA={loopProgressA}
                loopProgressB={loopProgressB}
                activeLoopIdx={activeLoopIdx}
                forkTriggerThreshold={FORK_TRIGGER_THRESHOLD}
                goalForkRange={FORK_RANGE}
                barFillRange={BAR_FILL_RANGE}
            />
        </View>
    );

    const goalsBox =
        rules.length > 0 ? (
            <View
                backgroundColor={GOALS_SYSTEM_COLOR}
                borderRadius={16}
                padding={8}
            >
                <GoalsSystemContent
                    ruleSetDisplays={rules}
                    maxLabelWidth={maxLabelWidth}
                    updateLabelWidth={updateLabelWidth}
                    updateLabelHeight={updateLabelHeight}
                    updateGoalRowHeight={updateGoalRowHeight}
                    loopProgressA={loopProgressA}
                    loopProgressB={loopProgressB}
                    activeLoopIdx={activeLoopIdx}
                    goalForkThresholds={goalForkThresholds}
                    goalDotColor={toHex(GOALS_COLOR)}
                    goalForkRange={FORK_RANGE}
                    barFillRange={BAR_FILL_RANGE}
                    filledGoalCount={filledGoalCount}
                    monthlyGoalsCents={goalsTotalCents}
                />
            </View>
        ) : null;

    // --- Rebuild the vertical connector geometry from measured positions ---
    // (Mirrors OnboardingMoneyMapPreview's label-center math.)
    const contentPad = 8;
    const labelCenterX =
        contentPad + (maxLabelWidth > 0 ? maxLabelWidth / 2 : 0);

    const connectorCenters = useMemo(() => {
        const incomeLabel = labelInfo.income;
        const billsLabel = labelInfo.bills;
        if (incomeLabel == null || billsLabel == null) return [];
        const centers: Array<{ y: number; halfH: number }> = [
            {
                y:
                    boxTops.income +
                    contentPad +
                    incomeLabel.yInRow +
                    incomeLabel.height / 2,
                halfH: incomeLabel.height / 2,
            },
            {
                y:
                    boxTops.expenses +
                    contentPad +
                    billsLabel.yInRow +
                    billsLabel.height / 2,
                halfH: billsLabel.height / 2,
            },
        ];
        // Goal rows stack inside the goals box (YStack gap 8, box pad 8).
        const goalsGap = 8;
        let cumulativeY = boxTops.goals + contentPad;
        for (let g = 0; g < rules.length; g++) {
            const goalLabel = labelInfo[`goal-${g}`];
            const rowH = goalRowHeights[g];
            if (goalLabel == null || rowH == null) break;
            centers.push({
                y: cumulativeY + goalLabel.yInRow + goalLabel.height / 2,
                halfH: goalLabel.height / 2,
            });
            cumulativeY += rowH + goalsGap;
        }
        return centers;
    }, [labelInfo, goalRowHeights, boxTops, rules.length]);

    const lineSegments = useMemo(() => {
        const segs: Array<{ top: number; height: number }> = [];
        for (let i = 0; i < connectorCenters.length - 1; i++) {
            const current = connectorCenters[i];
            const next = connectorCenters[i + 1];
            if (current == null || next == null) continue;
            const top = current.y + current.halfH;
            const bottom = next.y - next.halfH;
            if (bottom > top) segs.push({ top, height: bottom - top });
        }
        return segs;
    }, [connectorCenters]);

    const centerYs = connectorCenters.map((c) => c.y);
    const evenThresholds = connectorCenters.map((_, i, arr) =>
        arr.length > 1 ? i / (arr.length - 1) : 0,
    );
    const colorBreakT = Math.min(evenThresholds[1] ?? 0.5, 0.9);
    const goalsHex = toHex(GOALS_COLOR);
    const connectorReady = connectorCenters.length >= 2;

    // The money-map boxes are ALWAYS shown; the section copy, product rows and
    // disclosures live in collapsibles that expand in place when `showText` is
    // true (staggered for a cute cascade). Boxes carry a constant bottom gap so
    // they stay evenly spaced when the copy is collapsed.
    return (
        // The preview lays its boxes out in `availableWidth = contentWidth - 2`
        // (left-aligned, 2px slack on the right). Mirror that here so the boxes
        // are the exact same width/position and the preview ↔ body swap doesn't
        // jerk horizontally.
        <YStack width="100%" paddingRight={2}>
            {connectorVisible && connectorReady && (
                <>
                    {lineSegments.map((seg, idx) => (
                        <View
                            key={`conn-line-${idx}`}
                            pointerEvents="none"
                            style={{
                                position: 'absolute',
                                left: labelCenterX - CONNECTOR_WIDTH / 2,
                                top: seg.top,
                                width: CONNECTOR_WIDTH,
                                height: Math.max(0, seg.height),
                                backgroundColor: '#FFFFFF',
                                zIndex: 5,
                            }}
                        />
                    ))}
                    {lineSegments.map((seg, idx) => (
                        <ConnectorDot
                            key={`conn-dot-${idx}`}
                            seg={seg}
                            evenThresholds={evenThresholds}
                            centerYs={centerYs}
                            colorBreakT={colorBreakT}
                            labelCenterX={labelCenterX}
                            loopProgress={loopProgressA}
                            goalsHex={goalsHex}
                        />
                    ))}
                </>
            )}
            <Collapsible
                open={showText}
                delay={COLLAPSIBLE_BASE_DELAY}
                scale={speedScale}
            >
                <SectionHeading
                    title="It starts with your paycheck"
                    body="Once your deposit arrives, your money system springs into action and puts your plan to work."
                />
            </Collapsible>
            <YStack
                paddingBottom={SYSTEM_GAP}
                onLayout={(e) => {
                    updateBoxTop('income', e.nativeEvent.layout.y);
                }}
            >
                {incomeBox}
            </YStack>

            <Collapsible
                open={showText}
                delay={COLLAPSIBLE_BASE_DELAY + 70}
                scale={speedScale}
            >
                <SectionHeading
                    title="Bills are always covered first"
                    body={`First, we set aside ${centsToFormattedDollars(billsTotalCents, false)} each month to cover housing, utilities, subscriptions, and other monthly bills.`}
                />
            </Collapsible>
            <YStack
                paddingBottom={SYSTEM_GAP}
                onLayout={(e) => {
                    updateBoxTop('expenses', e.nativeEvent.layout.y);
                }}
            >
                {expensesBox}
            </YStack>

            <Collapsible
                open={showText}
                delay={COLLAPSIBLE_BASE_DELAY + 140}
                scale={speedScale}
            >
                <ProductRow
                    icon={
                        <View
                            gap={12}
                            width={32}
                            height={32}
                            backgroundColor="$water"
                            borderRadius={8}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <FruitfulLogo
                                width={16}
                                height={16}
                                color="$waterDarkest"
                            />
                        </View>
                    }
                    title="Core"
                    subtitle="Fruitful Cash Account"
                    amount="4.00% APY¹"
                />
            </Collapsible>
            <Collapsible
                open={showText}
                delay={COLLAPSIBLE_BASE_DELAY + 210}
                scale={speedScale}
            >
                <SectionHeading
                    title="Spend without second-guessing"
                    body={`Then, you're free to use ${centsToFormattedDollars(spendTotalCents, false)} for everyday spending, knowing your bills are covered and your goals are still on track for the month.`}
                />
            </Collapsible>
            <Collapsible
                open={showText}
                delay={COLLAPSIBLE_BASE_DELAY + 280}
                scale={speedScale}
            >
                <ProductRow
                    icon={
                        <Image source={ChargeCardIcon} width={38} height={32} />
                    }
                    title="Spend"
                    subtitle="Fruitful Charge Card"
                    amount="2% cash back²"
                />
            </Collapsible>

            <Collapsible
                open={showText}
                delay={COLLAPSIBLE_BASE_DELAY + 350}
                scale={speedScale}
            >
                <SectionHeading
                    title="Make progress without the stress"
                    body={`Finally, ${centsToFormattedDollars(goalsTotalCents, false)} goes toward your goals, funding what matters most first, then automatically moving to what's next.`}
                />
            </Collapsible>
            {goalsBox != null && (
                <YStack
                    paddingBottom={SYSTEM_GAP}
                    onLayout={(e) => {
                        updateBoxTop('goals', e.nativeEvent.layout.y);
                    }}
                >
                    {goalsBox}
                </YStack>
            )}

            <Collapsible
                open={showText}
                delay={COLLAPSIBLE_BASE_DELAY + 420}
                scale={speedScale}
            >
                <YStack gap={12} paddingTop={16} paddingHorizontal={2}>
                    <BankingGeneralDisclosure color="$gray1000" />
                    <CashApyDisclosure superscript={1} color="$gray1000" />
                    <CashBackDisclosure superscript={2} color="$gray1000" />
                </YStack>
            </Collapsible>
        </YStack>
    );
};

export const HowSystemWorksContent = ({
    incomeTotalCents,
    billsTotalCents,
    spendTotalCents,
    goalsTotalCents,
    ruleSetDisplays,
    onDone,
}: {
    incomeTotalCents: number;
    billsTotalCents: number;
    spendTotalCents: number;
    goalsTotalCents: number;
    ruleSetDisplays: RuleSetDisplay[] | null;
    onDone: () => void;
}) => {
    const { bottom: safeBottom } = useSafeAreaInsets();

    return (
        <View flex={1} width="100%">
            <ScrollView
                flex={1}
                width="100%"
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{
                    paddingTop: 24,
                    // Leave room for the floating "Got it" button.
                    paddingBottom: Math.max(safeBottom, 24) + 72,
                }}
            >
                <YStack
                    width="100%"
                    maxWidth={480}
                    alignSelf="center"
                    backgroundColor="$white"
                    borderRadius={24}
                    padding={16}
                    gap={20}
                >
                    <H5Emphasized textAlign="center">
                        How your system works
                    </H5Emphasized>
                    <HowSystemWorksBody
                        incomeTotalCents={incomeTotalCents}
                        billsTotalCents={billsTotalCents}
                        spendTotalCents={spendTotalCents}
                        goalsTotalCents={goalsTotalCents}
                        ruleSetDisplays={ruleSetDisplays}
                    />
                </YStack>
            </ScrollView>

            {/* "Got it" button — hovers, bottom-anchored to the right. */}
            <View
                position="absolute"
                bottom={Math.max(safeBottom, 16)}
                right={16}
                zIndex={11}
            >
                <Button
                    width="auto"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.SMALL}
                    text="Got it"
                    onPress={onDone}
                />
            </View>
        </View>
    );
};
